import {isFeatureEnabled} from '@github-ui/feature-flags'
import {replaceTokensDifferentially} from '@github-ui/filter-query'
import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import {getQuery} from '@github-ui/list-view-items-issues-prs/Query'
import type {VariableTransformer} from '@github-ui/relay-route/types'
import {VALUES} from '@github-ui/repository-milestone/values'
import type {Environment} from 'react-relay'

import {CUSTOM_VIEW, REPOSITORY_VIEW} from '../constants/view-constants'
import {getIssueSearchTypeFromEnvironment} from './issue-search-type'
import {addPagingParams} from './urls'

/**
 * Gets the `best_match_sort_enabled` value for determining default sort behavior.
 * This is the combined result of the `issues_default_best_match_sort` feature flag AND semantic search eligibility,
 * computed by Rails in `secondary_best_match_sort_enabled?`.
 *
 * Reads from `environment.options.bestMatchSortEnabled` which is set by RelayRoute.tsx from the payload.
 *
 * @param environment - Relay environment with options set by RelayRoute
 * @returns `true` if best match sort should be the default, `false` otherwise
 */
function getBestMatchSortEnabled(environment?: Environment): boolean {
  const envOptions = environment?.options as {bestMatchSortEnabled?: boolean} | undefined
  return envOptions?.bestMatchSortEnabled ?? false
}

/**
 * Behind `issues_best_match_requires_free_text`, the relevance default only applies to queries that
 * contain real free text. Mirrors `Search::Queries::FreeTextDetector` on the Rails side.
 */
export function requireFreeTextForBestMatch(): boolean {
  return isFeatureEnabled('issues_best_match_requires_free_text')
}

function applyIssueSearchType(variables: Record<string, unknown>, environment?: Environment): void {
  const issueSearchType = getIssueSearchTypeFromEnvironment(environment)
  if (issueSearchType) {
    variables['type'] = issueSearchType
  }
}

function applyShowIssueFieldPills(variables: Record<string, unknown>): void {
  variables['showIssueFieldPills'] = true
}

export const variablesIndex: VariableTransformer<string> = (variables, routeParams, environment?) => {
  const newVariables = variables
  if (Object.keys(routeParams.pathParams).length > 0) {
    const urlQuery = routeParams.searchParams.get('q')

    const defaultQuery = `${urlQuery || REPOSITORY_VIEW.query}`

    const customQuery = CUSTOM_VIEW.query({
      author: routeParams.pathParams['author'],
      assignee: routeParams.pathParams['assignee'],
      mentioned: routeParams.pathParams['mentioned'],
      label: routeParams.pathParams['label'],
    })

    newVariables['query'] = getQuery(
      customQuery && !urlQuery ? `${defaultQuery} ${customQuery}` : defaultQuery,
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        owner: routeParams.pathParams['owner']!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        name: routeParams.pathParams['repo']!,
      },
      getBestMatchSortEnabled(environment),
      requireFreeTextForBestMatch(),
    )

    addPagingParams(routeParams.searchParams, newVariables)

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['owner'] = routeParams.pathParams['owner']!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['name'] = routeParams.pathParams['repo']!
    applyIssueSearchType(newVariables, environment)
    applyShowIssueFieldPills(newVariables)
  }

  return newVariables
}

function createVariableTransformers<const T extends Record<string, VariableTransformer<string>>>(transformers: T) {
  return transformers
}
export const VARIABLE_TRANSFORMERS = createVariableTransformers({
  '/:owner/:repo/issues/created_by/app/:author': (variables, routeParams, environment?) => {
    const newRouteParams = {...routeParams}
    newRouteParams.pathParams = {
      ...routeParams.pathParams,
      author: `app/${routeParams.pathParams['author']}`,
    }
    const newVariables = variablesIndex(variables, newRouteParams, environment)
    return newVariables
  },
  '/:owner/:repo/milestone/:number': (variables, routeParams) => {
    const newVariables = variables
    newVariables['id'] = REPOSITORY_VIEW.id
    newVariables['first'] = VALUES.issuesPageSize
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['owner'] = routeParams.pathParams['owner']!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['name'] = routeParams.pathParams['repo']!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const milestoneNumberParam = routeParams.pathParams['number']!

    const showClosed = routeParams.searchParams.get('closed') === '1'

    const issueState = showClosed ? 'closed' : 'open'

    newVariables['skip'] = 0
    const pageParam = routeParams.searchParams.get('page')
    if (pageParam && isValidInteger(pageParam) && parseInt(pageParam, 10) > 1) {
      newVariables['skip'] = (parseInt(pageParam, 10) - 1) * VALUES.issuesPageSize
    }

    if (milestoneNumberParam) {
      const milestoneNumber = parseInt(milestoneNumberParam, 10)
      newVariables['number'] = isValidInteger(milestoneNumberParam) ? milestoneNumber : 0
      newVariables['query'] =
        `state:${issueState} milestone-number:${newVariables['number']} archived:false sort:milestone_prio-desc`
    }

    return newVariables
  },
  '/:owner/:repo/milestones': (variables, routeParams) => {
    const newVariables = variables
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['owner'] = routeParams.pathParams['owner']!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['name'] = routeParams.pathParams['repo']!
    const showClosed = routeParams.searchParams.get('state') === 'closed'
    newVariables['state'] = showClosed ? 'CLOSED' : 'OPEN'
    // first and skip are both derived from milestonePageSize so pages stay contiguous.
    newVariables['first'] = VALUES.milestonePageSize
    newVariables['skip'] = 0
    const pageParam = routeParams.searchParams.get('page')
    if (pageParam && isValidInteger(pageParam) && parseInt(pageParam, 10) > 1) {
      newVariables['skip'] = (parseInt(pageParam, 10) - 1) * VALUES.milestonePageSize
    }
    const sort = routeParams.searchParams.get('sort')
    const direction = routeParams.searchParams.get('direction')
    if (sort && direction) {
      if (sort === 'due_date') {
        newVariables['orderField'] = 'DUE_DATE'
      }
      if (sort === 'completeness') {
        newVariables['orderField'] = 'COMPLETED'
      }
      if (sort === 'title') {
        newVariables['orderField'] = 'ALPHABETICAL'
      }
      if (sort === 'count') {
        newVariables['orderField'] = 'ISSUES'
      }
      if (direction === 'asc') {
        newVariables['orderDirection'] = 'ASC'
      } else {
        newVariables['orderDirection'] = 'DESC'
      }
    }

    return newVariables
  },
  '/:owner/:repo/milestones/new': (variables, routeParams) => {
    const newVariables = variables
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['owner'] = routeParams.pathParams['owner']!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newVariables['name'] = routeParams.pathParams['repo']!
    return newVariables
  },
  '/:owner/:repo/milestones/:number/edit': (variables, routeParams) => {
    const newVariables = variables

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const milestoneNumberParam = routeParams.pathParams['number']!

    newVariables['owner'] = routeParams.pathParams['owner']
    newVariables['name'] = routeParams.pathParams['repo']

    if (milestoneNumberParam) {
      const milestoneNumber = parseInt(milestoneNumberParam, 10)
      newVariables['number'] = isValidInteger(milestoneNumberParam) ? milestoneNumber : 0
    }

    return newVariables
  },
  '/:owner/:repo/labels/:label': (variables, routeParams, environment?) => {
    const newVariables = variables
    if (Object.keys(routeParams.pathParams).length > 0) {
      const urlQuery = routeParams.searchParams.get('q')

      const baseQuery = QUERIES.open
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const defaultLabelQuery = replaceTokensDifferentially(baseQuery, [routeParams.pathParams['label']!], 'label')

      newVariables['query'] = getQuery(
        urlQuery?.trim() || defaultLabelQuery,
        {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          owner: routeParams.pathParams['owner']!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          name: routeParams.pathParams['repo']!,
        },
        getBestMatchSortEnabled(environment),
        requireFreeTextForBestMatch(),
      )

      addPagingParams(routeParams.searchParams, newVariables)

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newVariables['owner'] = routeParams.pathParams['owner']!
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newVariables['name'] = routeParams.pathParams['repo']!
      applyShowIssueFieldPills(newVariables)
    }

    return newVariables
  },
  '/:owner/:repo/issues': variablesIndex,
  '/:owner/:repo/issues/created_by/:author': variablesIndex,
  '/:owner/:repo/issues/assigned/:assignee': variablesIndex,
  '/:owner/:repo/issues/mentioned/:mentioned': variablesIndex,
  '/:owner/:repo/issues/:number': (variables, routeParams) => {
    const newVariables = variables

    newVariables['count'] = 15
    const timelinePage = routeParams.searchParams.get('timeline_page')
    if (timelinePage && isValidInteger(timelinePage)) {
      const pageNumber = parseInt(timelinePage, 10)
      if (pageNumber > 0) {
        newVariables['skip'] = 15 + (pageNumber - 1) * 150
        newVariables['count'] = 150
      }
    }

    return newVariables
  },
})

export const isValidInteger = (value: string) => {
  //regex to validate if input string is an integer
  return /^\s*-?\d+\s*$/.test(value)
}
